import { findAction, findDraftField, findField, inputOf, withinModal } from './anchors';
import {
  acceptsBeforeSave, answerQuestion, cancelQuestion, confirmQuestion, declines, dismissNotices,
  curtained, NO_LABELS, questionAsked, settleDialog, waitForCurtain, YES_LABELS,
} from './dialogs';
import { bridge, detailState } from './bridge';
import { OptionNotFound, selectOption, selectRadio, setValue, toggleCheck } from './input';
import { hasSearchPopup, pairedName, pickFromSearchPopup, requireChoice, sameName } from './popup';
import { similarity } from './hangul';
import { onScreen, onScreenAll } from './visible';

export { openScreen } from './screen';

/**
 * 1차 목표는 조회와 안내다. 쓰기는 프롬프트가 아니라 여기서 막는다 —
 * 프롬프트는 우회되지만 이 목록은 우회되지 않는다.
 */
/** 가림막이 걷히기를 기다려 주는 시간. */
const CURTAIN_MS = 5000;
/** 클릭 뒤 알림이 뜨기까지 주는 시간. */
const NOTICE_MS = 250;
/** 화면 경로가 먼저 바뀌고 검색조건 DOM·초기 상태는 나중에 생긴다. */
const FIELD_WAIT_MS = 5000;
const FIELD_POLL_MS = 25;
/** 저장·삭제의 확인 팝업. 화면 안에서 바로 뜨므로 짧게 본다. */
const CONFIRM_WAIT_MS = 3000;
/**
 * 문서를 잇는 버튼이 여는 창.
 *
 * 이쪽은 **서버를 다녀온 뒤** 열린다 — 견적서의 `자재청구서` 는 6초쯤 걸렸고, 3초만
 * 기다린 우리는 `창이 뜨지 않았다` 고 답했다. 창은 잘 뜨고 있었다.
 */
const MODAL_WAIT_MS = 12000;
/**
 * **엘맨 매니저를 띄우고 이어 하는 단추**(보수현장 정보센터연동). 「매니저가 실행중이 아닙니다 — 실행하시겠습니까?」에
 * 예로 답하면 앱이 `emr://` 로 매니저를 띄우고, 매니저가 붙은 뒤에야 일이 이어진다(개발 테넌트 실측 약 20초).
 * 그동안 일괄/단일 창·완료 알림을 계속 본다. 매니저 일은 네트워크가 아니라 가림막(로딩)으로만 보인다.
 */
const MANAGER_WAIT_MS = 90000;
/** 가림막이 떠 있는 동안 더 보는 끝 — 정보센터 일괄·검사 연동은 호기마다 매니저를 부른다. */
const LONG_WORK_MS = 10 * 60 * 1000;
/** 창 안에서 검색을 걸고 목록이 바뀌기를 기다리는 시간. */
const SEARCH_MS = 3000;
// 저장 한 번에 확인이 몇 번까지 뜨나. 인사등록이 둘이다.
const CONFIRM_ROUNDS = 3;
// 두 번째부터는 이미 떠 있거나 곧 뜬다. 없으면 빨리 나간다.
const FOLLOWUP_CONFIRM_MS = 1500;
const CONFIRM_POLL_MS = 25;

/** 화면 글자. `innerText` 가 없는 환경(테스트)에서는 `textContent` 로 떨어진다. */
function readText(node: HTMLElement): string {
  return node.innerText ?? node.textContent ?? '';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

export const READ_ONLY_ACTIONS = ['retrieve', 'print', 'excel'] as const;
/** 저장 전 초안을 시작하는 것만 허용한다. save/delete는 여기 없다 — 아래 자율 목록이다. */
export const DRAFT_ACTIONS = ['new'] as const;
/**
 * 사용자가 **Auto 를 켰을 때만** 열리는 목록.
 *
 * 켜졌는지는 프롬프트가 아니라 호출자가 넘긴 값으로 판단한다 — 프롬프트는 우회되지만
 * 인자는 우회되지 않는다. 런타임은 사용자가 패널에서 체크한 그 값만 넘긴다.
 */
export const AUTO_ACTIONS = ['save', 'delete'] as const;

/** 알림을 닫고 계속 계산하면 안 되는 조회 선행조건 오류인가. */
export function isBlockingNotice(notice: string): boolean {
  return /아무것도\s*선택되지\s*않았습니다/.test(notice)
    || /필수.+(?:선택|입력).+(?:않았습니다|바랍니다)/.test(notice)
    // **화면이 먼저 달라는 것**도 조회를 막는다. `먼저 창고를 선택해주세요` 로 막힌
    // 조회가 0행으로 돌아왔고, 답은 `재고 자료가 없습니다` 였다 — 사람은 재고가
    // 없다고 읽는다. 막힌 것과 없는 것은 다르다.
    || /먼저.+(?:선택|입력)(?:해\s*주세요|하세요|하십시오|하십시요)/.test(notice);
}

/** 초안 값은 화면 상태의 `data` 안에 있다. 같은 이름의 검색조건과 섞으면 안 된다. */
export type FillOptions = { draft?: boolean };

/**
 * 값을 재는 자리. 초안이면 화면의 **상세 상태**다.
 *
 * 상세를 부르는 이름은 화면마다 다르다 — 휴가·출장은 `data`, 견적서는 `content` 다.
 */
function scopeState(draft: boolean): Record<string, unknown> | null {
  if (!draft) return bridge().pageState();
  return detailState();
}

/**
 * **넣은 값을 화면이 받았나.** 그릇이 무엇이든 상관없이 본다.
 *
 * 초안이 어디 사는지는 화면마다 다르다 — 스칼라일 수도, `data` 같은 상세 객체일 수도,
 * 표의 행일 수도 있다. 한 곳을 고르지 않고 전부 훑어 그 값이 있는지만 본다. 어느
 * 행인지 맞히려 들지 않는 것이 요점이다 — 틀리면 남의 행을 보고 통과시킨다.
 *
 * 화면이 모양을 바꿔 담는다(`5000`→`5,000`, `20260916`→`2026-09-16`). 그래서 비교는
 * 공백·쉼표·붙임표를 떼고 한다.
 */
function landed(name: string, wanted: string): boolean {
  const want = looseValue(wanted);
  if (!want) return true;
  const holds = (record: Record<string, unknown> | null | undefined): boolean => (
    Boolean(record) && typeof record === 'object' && looseValue(record![name]) === want
  );
  if (holds(detailState())) return true;
  const state = bridge().pageState();
  if (holds(state)) return true;
  return Object.values(state ?? {}).some((value) => Array.isArray(value)
    && value.some((row) => holds(row as Record<string, unknown>)));
}

/** 공백·쉼표·붙임표를 뗀 알맹이. 화면 표기와 요청 값을 같은 자로 잰다. */
function looseValue(raw: unknown): string {
  return String(raw ?? '').replace(/[\s,\-/().]/g, '').toLowerCase();
}

function stateMatches(name: string, wanted: string, draft = false): boolean {
  const state = scopeState(draft);
  return state !== null
    && Object.prototype.hasOwnProperty.call(state, name)
    && String(state[name] ?? '') === wanted;
}

/**
 * 검색조건이 렌더되거나, 보이지 않는 조건이 화면 상태에 이미 적용될 때까지 기다린다.
 *
 * 휴가신청서의 사원 조건처럼 권한에 따라 입력칸을 숨기면서 로그인 사용자를 상태에
 * 자동으로 넣는 화면이 있다. 이 경우 입력할 필요도, 입력할 DOM도 없다.
 */
/** 화면이 멎을 때까지 기다린다. 안 멎어도 시한이 되면 넘어간다 — 대기가 실패는 아니다. */
async function settle(timeoutMs: number): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  while ((bridge().busy?.() ?? false) && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(FIELD_POLL_MS);
  }
}

/** 어느 칸·어느 화면에서 났는지 실패에 적어 둔다. 그것 없이는 재현할 수 없다. */
function stampOption(error: OptionNotFound, name: string): void {
  error.field = name;
  error.route = bridge().currentPath() ?? undefined;
}

async function readyCondition(
  name: string,
  wanted: string,
  timeoutMs: number,
  draft: boolean,
): Promise<{ anchor?: HTMLElement; applied: boolean }> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    if (stateMatches(name, wanted, draft)) return { applied: true };
    const anchor = draft ? findDraftField(name) : findField(name);
    if (anchor) return { anchor, applied: false };
    if (Date.now() >= deadline) return { applied: false };
    await sleep(FIELD_POLL_MS);
  }
}

/** 코드로 볼 만한 글자인가. 한글이 섞이면 사람 말이지 코드가 아니다. */
function looksLikeCode(value: string): boolean {
  return /^[0-9A-Za-z_-]{1,20}$/.test(value.trim());
}

/** 코드 칸(`actcd`)의 짝이 되는 이름 칸 이름(`actnm`). 코드 칸이 아니면 없다. */
function namePartner(name: string): string | null {
  const stem = name.replace(/(cd|id|code)$/i, '');
  if (stem === name) return null;
  return `${stem}nm`;
}

/** 이름 칸(`actnm`)의 짝이 되는 코드 칸 이름(`actcd`). 이름 칸이 아니면 없다. */
function codePartner(name: string): string | null {
  const stem = name.replace(/(nm|name)$/i, '');
  if (stem === name) return null;
  return `${stem}cd`;
}

/**
 * **등록되지 않은 것을 이름만으로 적는다.** 코드 칸은 비운 채로 둔다.
 *
 * 출장보고서가 그렇다 — 등록되지 않은 현장으로 출장가는 일이 있고, 그때 사람은
 * 현장코드를 비우고 현장명만 친다. 팝업은 그 이름을 모르니 **가장 비슷한 남의 현장**을
 * 골라 준다. 실제로 `무등산타워` 가 `무등상사` 로 들어갔다.
 *
 * 허락은 화면이 한다 — 이름 칸이 직접 쳐지는 화면에서만 이 길이 열린다. 이름 칸을
 * 읽기 전용으로 둔 화면(대부분)에서는 값이 상태에 안 붙어 그대로 실패한다.
 */
async function writeNameOnly(name: string, value: string, draft: boolean): Promise<boolean> {
  // **코드 칸에는 이 길이 열리지 않는다.** 이름을 코드 자리에 쓰면 생 글자가 남고,
  // 아래의 「코드가 안 지워졌으면」 안전장치는 짝이 없어 돌지 않는다. 고장접수의
  // 호기가 그랬다 — 사람이 `1호기` 라고 답하면 그 현장에 등록된 이름은 `1-1`~`1-5`
  // 뿐인데, 그 글자를 `equpcd` 에 밀어 넣고 **성공이라 답해** 팝업이 이미 손에 쥔
  // 후보 다섯을 버렸다. 되묻지도 못하고 초안이 죽었다.
  //
  // 대신 **짝이 되는 이름 칸으로 넘긴다.** 출장보고서는 미등록 현장을 그렇게 적는다.
  // 이름 칸이 아예 없는 화면(호기)에서만 이 길이 닫힌다.
  const named = namePartner(name);
  // 코드꼴 값은 코드 칸의 것이다 — 고장내용 `008`(사람갇힘)이 그렇게 들어간다.
  // 팝업 검색칸은 **이름만** 훑어서 코드로 물으면 0건이 되는데, 그렇다고 못 적을
  // 값은 아니다. 막을 것은 사람 말(`1호기`)이지 코드가 아니다.
  if (named && !looksLikeCode(value)) {
    const target = draft ? findDraftField(named) : findField(named);
    const targetInput = target && inputOf(target);
    if (!targetInput) return false;
    return writeNameOnly(named, value, draft);
  }
  const anchor = draft ? findDraftField(name) : findField(name);
  const input = anchor && inputOf(anchor);
  if (!input || input.readOnly || input.disabled) return false;
  // 팝업이 이미 남의 코드를 넣어 두었으면 지운다. 코드가 남으면 이름만 바꾼 셈이 되어
  // 저장은 **남의 현장**으로 들어간다.
  const partner = codePartner(name);
  if (partner) {
    const codeAnchor = draft ? findDraftField(partner) : findField(partner);
    const codeInput = codeAnchor && inputOf(codeAnchor);
    if (codeInput && !codeInput.readOnly && !codeInput.disabled) {
      codeInput.focus();
      await setValue(codeInput, '');
      codeInput.blur();
    }
  }
  input.focus();
  await setValue(input, value);
  input.blur();
  const deadline = Date.now() + CONFIRM_WAIT_MS;
  while (!stateMatches(name, value, draft) && Date.now() < deadline) await sleep(FIELD_POLL_MS);
  if (!stateMatches(name, value, draft)) return false;
  // 코드가 안 지워졌으면 이름만 넣은 것이 아니다 — 조용히 넘기면 남의 현장으로 저장된다.
  if (partner && String((scopeState(draft) ?? {})[partner] ?? '').trim()) return false;
  return true;
}

/**
 * 팝업이 내놓은 후보 중에 **사람이 말한 그것**이라 할 만한 것이 있는가.
 *
 * 있으면 이름만 적는 길로 새지 않고 되묻는다 — 거기 있는데 못 고른 것과 아예 없는 것은
 * 다른 일이다.
 */
function popupHasNear(seen: string[], value: string): boolean {
  return seen.some((label) => similarity(value, label) >= 0.9 || sameName(label, value));
}

/**
 * 그 칸이 고를 수 있는 것들. **팝업을 열어 직접 읽는다.**
 *
 * 현장마다 다른 목록(호기)이나 앞 칸에 매달린 목록(고장부위상세)은 미리 담을 수
 * 없다. 그렇다고 `화면에서 직접 골라 주세요` 로 돌려보내면 사람은 ERP 화면으로
 * 간다 — 팝업은 열리고 행도 거기 있는데.
 *
 * 없을 것이 뻔한 값을 물어 팝업이 되돌려 주는 후보를 그대로 받는다. 여는 절차와
 * 「검색어를 비우고 다시 훑기」가 이미 그 길에 다 들어 있어서, 따로 지으면 두 벌이 된다.
 */
const NOTHING = '없을리없는값즤즤';

export async function popupOptions(name: string, draft = false): Promise<string[]> {
  const anchor = draft ? findDraftField(name) : findField(name);
  if (!anchor || !hasSearchPopup(anchor)) return [];
  try {
    await pickFromSearchPopup(anchor, NOTHING);
    return [];
  } catch (error) {
    return error instanceof OptionNotFound ? error.options : [];
  }
}

/**
 * 콤보 칸이 고를 수 있는 것들.
 *
 * 초안 콤보 59개 중 **40개**가 선택지를 서버에서 받아 온다 — 정적 파싱으로는 못 읽고,
 * 그 칸들에서 계획이 값을 지어내거나 헛되묻는다. 화면을 열어 직접 보는 수밖에 없다.
 *
 * `popupOptions` 와 같은 수법이다. 없을 것이 뻔한 값을 골라 달라 하고, 못 골랐다는
 * 오류가 들고 오는 후보를 받는다 — 여는 절차와 「열려 있는 지금 읽기」가 이미 그 길에
 * 들어 있어서 따로 지으면 두 벌이 된다.
 */
export async function comboOptions(name: string, draft = false): Promise<string[]> {
  const anchor = draft ? findDraftField(name) : findField(name);
  if (!anchor) return [];
  try {
    await selectOption(anchor, NOTHING);
    return [];
  } catch (error) {
    return error instanceof OptionNotFound ? error.options : [];
  }
}

/**
 * **조회 조건에서 「이 칸을 비워라」를 뜻하는 값.** 빈 값(`''`)은 「이 조건은 걸지 않는다」라서 건너뛴다.
 * 화면이 미리 채워 둔 칸을 비워야 할 때 쓴다 — 통합문서는 담당자를 로그인한 사람으로 채워 내 문서만
 * 보였다(2026-09-26). 스킬의 고정 조건이 이 값을 건다.
 */
export const CLEAR = '(비움)';

/** 칸과 그 짝(코드·이름)의 입력칸을 비운다. 잠긴 칸은 건드리지 않는다. */
async function clearField(name: string, draft: boolean): Promise<void> {
  for (const key of [name, codePartner(name), namePartner(name)].filter((one): one is string => Boolean(one))) {
    const anchor = draft ? findDraftField(key) : findField(key);
    const input = anchor && inputOf(anchor);
    if (!input || input.readOnly || input.disabled || !input.value) continue;
    input.focus();
    // eslint-disable-next-line no-await-in-loop
    await setValue(input, '');
    input.blur();
  }
}

export async function fillConditions(
  values: Record<string, string>,
  waitMs = FIELD_WAIT_MS,
  options: FillOptions = {},
): Promise<void> {
  const draft = Boolean(options.draft);
  for (const [name, value] of Object.entries(values)) {
    if (value === CLEAR) {
      // eslint-disable-next-line no-await-in-loop
      await clearField(name, draft);
      // eslint-disable-next-line no-continue
      continue;
    }
    // 빈 값은 조건을 걸지 않겠다는 뜻이다. 선택 칸에 그것을 넣으려다
    // `wkactnm 칸의 선택지  을 찾지 못했다. 있는 것: 자사, 현대, 상주…` 로 죽었다.
    // 초안은 다르다 — 거기서 빈 값은 그 칸을 비우라는 뜻일 수 있다.
    if (!draft && !String(value ?? '').trim()) continue;
    // **앞 조건이 조회를 깨웠을 수 있다.** 급여현황은 `월` 을 바꾸면 스스로 다시
    // 조회하는데, 그 사이에 콤보를 누르면 클릭이 삼켜져 목록이 영영 열리지 않는다 —
    // `spjangcd 칸에 고를 목록이 없다` 로 턴이 끝난다. 화면을 따로 열어 누르면
    // 멀쩡히 열두 개가 뜨므로, 없는 칸을 찾는 문제가 아니라 시점 문제다.
    // 조회 시작 전 대기(`Session.retrieve`)는 첫 조건 앞에서만 돈다.
    // eslint-disable-next-line no-await-in-loop
    await settle(waitMs);
    // eslint-disable-next-line no-await-in-loop
    const ready = await readyCondition(name, value, waitMs, draft);
    if (ready.applied) continue;
    let anchor = ready.anchor;
    if (!anchor) throw new Error(`${draft ? '초안 칸' : '검색조건'} ${name} 이 화면에 없다`);
    // 사람은 이름을 말한다(`광주현장`). 화면은 이름 칸을 읽기 전용으로 두고 짝이 되는
    // 코드 칸에서 고르게 하는 일이 많다 — 그러면 이름 칸에 밀어 넣어도 아무 일도 없다.
    const partner = codePartner(name);
    if (partner) {
      const partnerAnchor = draft ? findDraftField(partner) : findField(partner);
      if (partnerAnchor && hasSearchPopup(partnerAnchor)) anchor = partnerAnchor;
    }
    const input = inputOf(anchor);
    if (input) {
      // 현장·담당자처럼 검색 팝업이 걸린 칸은 **쳐 넣으면 안 된다.** 코드 칸에 이름을
      // 그대로 밀어 넣으면 `actcd=광주` 같은 값이 상태에 남아 검증까지 통과한다.
      // 코드와 이름은 팝업에서 고른 행이 함께 넣어 줄 때만 옳게 들어간다.
      if (hasSearchPopup(anchor)) {
        let seen: string[] = [];
        try {
          seen = await pickFromSearchPopup(anchor, value);
        } catch (error) {
          // **바빠서 못 연 것과 목록에 없는 것은 다르다.** 둘 다 후보가 비어 오지만,
          // 앞의 것을 미등록으로 보면 팝업이 열렸다면 골랐을 자리에 생 글자만 남는다 —
          // 화면이 잠시 바빴다는 이유로 문서가 달라진다. 이유가 붙어 오면 그대로 던지고,
          // 대안 없는 차단으로 처리하게 둔다(스펙 §5.3·§5.8).
          const neverOpened = error instanceof OptionNotFound
            && error.reason === '팝업이 안 열렸다';
          // 목록에 아예 없는 것을 물었을 수 있다. 이름만으로 적을 수 있는 화면이면 그렇게 적는다.
          if (!neverOpened && error instanceof OptionNotFound
            && !popupHasNear(error.options ?? [], value)) {
            // eslint-disable-next-line no-await-in-loop
            if (await writeNameOnly(name, value, draft)) continue;
          }
          if (error instanceof OptionNotFound) stampOption(error, name);
          throw error;
        }
        // 팝업 경로는 화면마다 다르게 끝난다. 마지막 판정은 하나뿐이다 — 값이 들어왔는가.
        // 안 들어왔는데 넘어가면 빈 칸을 채웠다고 보고하게 된다.
        const filled = scopeState(draft) ?? {};
        if (!String(filled[name] ?? '').trim()) {
          // **본 것이 있으면 그것을 들려 보낸다.** 후보 없이 실패하면 사용자는 무엇을
          // 말해야 하는지 모른 채 화면으로 가야 한다 — 부서처럼 목록이 서른아홉 개인
          // 칸에서도 그랬다.
          if (seen.length) {
            const error = new OptionNotFound(value, seen.slice(0, 5));
            stampOption(error, name);
            throw error;
          }
          throw new Error(`${name} 을 검색 팝업에서 고르지 못했습니다.`);
        }
        // 화면이 스스로 다른 행을 확정했을 수 있다(`금호빌딩` 은 두 곳이다).
        // 짝이 되는 이름이 어긋나면 후보를 모아 사용자에게 고르게 한다.
        // 이름 칸을 물었을 때는 짝이 없다(`actnm` 의 stem 은 자기 자신이다). 그때는
        // **들어온 그 이름**을 본다 — 이 자리를 비워 둔 동안 `무등산타워` 가 `무등상사` 로
        // 들어가고도 통과했다.
        const named = pairedName(name, filled)
          ?? (/(nm|name)$/i.test(name) ? String(filled[name] ?? '') : null);
        if (named !== null && named.trim() && !sameName(named, value)) {
          // 비슷한 남의 것을 집어 온 것이다. 목록에 그럴듯한 것이 없으면 등록되지 않은
          // 이름으로 보고, 이름만 적을 수 있는 화면이면 그렇게 고쳐 적는다.
          // eslint-disable-next-line no-await-in-loop
          if (!popupHasNear(seen, value) && await writeNameOnly(name, value, draft)) continue;
          // 고르는 동안 본 이름을 그대로 쓴다. 다시 열려고 하면 방금 닫힌 팝업이
          // 더블클릭을 씹어 후보 없는 질문이 나간다 — 견적서에서 그랬다.
          const options = seen.length ? seen : [];
          if (options.length) {
            const error = new OptionNotFound(value, options);
            stampOption(error, name);
            throw error;
          }
          await requireChoice(anchor, value).catch((error) => {
            if (error instanceof OptionNotFound) stampOption(error, name);
            throw error;
          });
        }
        continue;
      }
      // 화면이 잠근 칸은 채울 수 없다 — 입금입력의 예금입금액은 예금 체크를 끄면
      // 읽기 전용이 된다. 조용히 실패하면 `bamt 가 반영되지 않았습니다` 로만 끝나
      // 무엇이 문제인지 알 수 없다.
      if (input.readOnly || input.disabled) {
        // **잠깐 잠겨 있는 것과 우리 몫이 아닌 것은 다르다.** 시간외근무보고서의
        // 작성일자는 신규를 누른 뒤 화면이 스스로 오늘을 넣는데, 그 사이에 우리가
        // 같은 값을 쓰려다 `잠가 둔 칸이다` 로 초안 전체가 죽었다 — 이미 그 값인데도.
        const settleBy = Date.now() + CONFIRM_WAIT_MS;
        while (!stateMatches(name, value, draft)
          && (input.readOnly || input.disabled)
          && Date.now() < settleBy) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(FIELD_POLL_MS);
        }
        if (stateMatches(name, value, draft)) continue;
        if (input.readOnly || input.disabled) {
          // **조건과 초안은 무게가 다르다.** 초안 칸이 잠겨 있으면 그 문서는 요청과
          // 다른 것이 되므로 알려야 하지만, 잠긴 **검색조건**은 못 거는 것뿐이다 —
          // 화면이 계산해서 보여 주는 칸(`wadcnt` 고장건수)에 계획이 값을 넣으려다
          // 조회 자체가 죽었고 사용자는 `요청을 처리하지 못했습니다` 만 받았다.
          if (!draft) continue;
          throw new Error(`${name} 은 지금 화면이 잠가 둔 칸이다. 그 값은 빼고 다시 내라`);
        }
      }
      // 금액·수량 칸(`FormatNumberTextBox`)은 입력을 내부 state 에만 담고 **blur 에서**
      // 부모로 올린다. 포커스 없이 값만 넣고 떠나면 화면 상태는 0 그대로다.
      input.focus();
      await setValue(input, value);
      input.blur();
      // **화면이 이미 그 값을 그리고 있으면 위젯은 아무 일도 안 한다.**
      //
      // 가지급등록은 신규를 누르면 지급일자 칸에 오늘을 그려 놓는데 **화면 상태는
      // 비어 있다.** 그 상태에서 「오늘」을 넣으라고 하면 위젯은 바뀐 게 없다고 보고
      // 부모에 안 올린다 — 칸은 맞는데 저장이 읽는 상태는 끝까지 빈 채다.
      //
      // 사람이 하듯 한 번 비웠다가 다시 넣으면 들어간다(2026-09-17 실측: 아무것도
      // 안 하면 `""`, 비웠다 다시 넣으면 `20260917`). 안 받았을 때만 도므로 지금
      // 통하는 칸은 하나도 안 건드린다.
      if (draft && String(value ?? '').trim()
        && !landed(name, value) && looseValue(input.value) === looseValue(value)) {
        // eslint-disable-next-line no-await-in-loop
        await setValue(input, '');
        // eslint-disable-next-line no-await-in-loop
        await sleep(FIELD_POLL_MS);
        // eslint-disable-next-line no-await-in-loop
        await setValue(input, value);
        input.blur();
      }
      continue;
    }

    // 입력칸이 없는 위젯은 셋이다. 라디오는 선택지에 data-el-option 이 달려
    // 있어 DOM 만 보고 가려낼 수 있다.
    if (anchor.querySelector('[data-el-option]')) { await selectRadio(anchor, value); continue; }

    // 남은 둘. 체크칸은 상태가 '1'/'0' 이고 부르는 쪽도 그렇게 말한다.
    // 콤보박스는 화면에 보이는 글자로 말한다.
    const read = () => String(scopeState(draft)?.[name] ?? '');
    const boolish = (x: string) => x === '1' || x === '0';
    if (boolish(read()) && boolish(value)) {
      // 이 앱의 체크박스는 입력 요소가 아니라 Lottie 위젯이고 스스로
      // `role="check-box"` 라고 밝힌다. 바깥을 누르면 안 먹는 자리가 있다.
      const box = anchor.querySelector<HTMLElement>('[role="check-box"]') ?? anchor;
      // 이 위젯은 **두 번 미룬다** — 직전에 만진 것이 입력칸이면 300ms, 화면이 서버를
      // 다녀오는 중이면 또 300ms. 눌러 놓고 바로 재면 언제나 "안 바뀌었다" 가 된다.
      const waitFlip = async (): Promise<boolean> => {
        const deadline = Date.now() + CONFIRM_WAIT_MS;
        while (read() !== value && Date.now() < deadline) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(FIELD_POLL_MS);
        }
        return read() === value;
      };
      await toggleCheck(box, read(), value);
      if (!await waitFlip()) {
        await toggleCheck(box, read(), value);
        await waitFlip();
      }
      // 눌러도 안 바뀌는 자리가 있다. 화면이 열어 둔 좁은 통로로 그 칸만 켠다 —
      // 임의 setState 가 아니라 `'1'`/`'0'` 하나를 바꾸는 길이다.
      //
      // ⚠ 이 길은 **화면의 뒷정리를 태우지 않는다.** 예금입금을 끄면 위젯은 금액칸도
      // 비우는데(`bamt: ''`), 이쪽으로 끄면 금액이 남아 총액이 어긋난다. 그래서
      // **진짜 클릭이 먼저**이고 이것은 마지막 수단이다.
      if (read() !== value) {
        const set = bridge().setCheck;
        if (set) await set(name, value === '1');
        await waitFlip();
      }
      if (read() !== value) throw new Error(`체크칸 ${name} 이 ${value} 로 바뀌지 않았다`);
      continue;
    }
    // 어느 칸이 걸렸는지는 여기만 안다. 부르는 쪽이 그것으로 되물을 수 있어야 한다.
    const had = read();
    try {
      await selectOption(anchor, value);
      // **고른 척하고 아무것도 안 들어가는 칸이 있다.** 고장처리결과의 고장부위
      // (`gregicd`)가 그렇다 — 무엇을 써도 성공으로 돌아오는데 상태는 빈 채로 남고,
      // 저장에서 `필수입력확인: 고장부위를 선택하세요` 로 막힌다. 입력칸이 없는 앵커라
      // 초안 검증도 그 칸을 건너뛰어, 사용자는 같은 되묻기를 몇 번이고 다시 받는다.
      //
      // 값이 코드로 바뀌어 들어가는 칸이 있으므로 글자가 같기를 요구하지는 않는다.
      // **비어 있던 칸이 그대로 비어 있으면** 아무 일도 안 난 것이다.
      // 화면이 그 칸을 상태로 들고 있을 때만 잰다. 상태에 없는 칸은 잴 근거가 없다.
      const scope = scopeState(draft);
      const tracked = Boolean(scope) && Object.prototype.hasOwnProperty.call(scope, name);
      if (tracked && !had.trim() && String(value ?? '').trim()) {
        const settleBy = Date.now() + CONFIRM_WAIT_MS;
        while (!read().trim() && Date.now() < settleBy) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(FIELD_POLL_MS);
        }
        if (!read().trim()) {
          throw new OptionNotFound(value, []);
        }
      }
    } catch (error) {
      // 선택지가 **하나도** 열리지 않았다면 고를 목록이 있는 칸이 아니다. 사원코드
      // `00456` 을 콤보박스로 다뤄 `있는 것: ` 이라는 빈 목록으로 실패한 적이 있다.
      // 그런 칸은 그냥 쳐 넣는 칸이다.
      if (error instanceof OptionNotFound && !error.options.length) {
        const box = anchor.querySelector('input, textarea');
        if (box instanceof HTMLInputElement || box instanceof HTMLTextAreaElement) {
          box.focus();
          // eslint-disable-next-line no-await-in-loop
          await setValue(box, value);
          box.blur();
          // eslint-disable-next-line no-await-in-loop
          await sleep(waitMs);
          if (read() === value) continue;
        }
      }
      if (error instanceof OptionNotFound) stampOption(error, name);
      throw error;
    }
  }
}

async function clickAction(
  id: string,
  includeBeforeNotices = true,
  options: { confirm?: boolean } = {},
): Promise<string[]> {
  const button = findAction(id);
  if (!button) throw new Error(`액션 ${id} 버튼이 화면에 없다`);

  // 가림막이 떠 있는 동안 누르면 그 판이 클릭을 먹는다. 누른 줄 알고 기다리게 된다.
  await waitForCurtain(Date.now() + CURTAIN_MS);
  // 앞선 자동조회가 남긴 단순 알림은 먼저 읽고 닫는다. 팝업 뒤의 버튼을 또 누르면
  // 같은 조회가 중복 실행될 수 있다.
  const beforeNotices = await dismissNotices();
  // 저장 직전, 화면이 「선택」 대화를 띄워 두는 일이 있다 — 공사등록의 「견적내용을
  // 불러오시겠습니까?」가 그렇다. `cancelQuestion` 은 그것을 취소(아니오)로 닫고 문구를
  // 준다. 표에 오른 것이면 아니오가 정답이니 문구만 남기고 저장을 이어 가고, 표에 없는
  // 것은 지금까지처럼 던져 사람에게 그 문구를 보인다(스펙 §5: 모르는 것은 멈춘다).
  const answeredBefore: string[] = [];
  const pending = questionAsked();
  if (pending && options.confirm && acceptsBeforeSave(pending)) {
    // 표에 오른 것만 예로 답한다(`ACCEPT_BEFORE_SAVE`). Auto 가 아니면 이 길로 안 온다.
    confirmQuestion();
    answeredBefore.push(pending);
    await settleDialog(FOLLOWUP_CONFIRM_MS);
  } else {
    const asked = cancelQuestion();
    if (asked) {
      if (!declines(asked)) throw new Error(`앱이 확인을 요구한다: ${asked}`);
      answeredBefore.push(asked);
    }
  }

  button.click();

  // 저장·삭제는 "정말 하시겠습니까" 를 띄운다. Auto 일 때만 그것을 확인으로 닫고,
  // 무엇을 승인했는지 문구를 남긴다.
  const approved: string[] = [];
  if (options.confirm) {
    // **확인이 하나가 아니다.** 인사등록은 저장 뒤에 근태를 만들지 묻고, 그다음 사용자
    // 등록으로 갈지 또 묻는다. 하나만 답하고 나가면 남은 것이 화면에 붙어 다음 걸음을
    // 막는다 — 가족 탭이 `앱이 확인을 요구한다` 로 죽었다.
    for (let round = 0; round < CONFIRM_ROUNDS; round += 1) {
      const deadline = Date.now() + (round === 0 ? CONFIRM_WAIT_MS : FOLLOWUP_CONFIRM_MS);
      let answered: string | null = null;
      while (!answered && Date.now() < deadline) {
        answered = confirmQuestion();
        // eslint-disable-next-line no-await-in-loop
        if (!answered) await sleep(CONFIRM_POLL_MS);
      }
      if (!answered) break;
      approved.push(answered);
      // **답한 창이 사라지기를 기다린다.** 안 기다리면 다음 라운드가 같은 창을 다시
      // 읽어 라운드를 태우고, 뒤이어 뜨는 진짜 이유를 못 본다(`settleDialog`).
      // eslint-disable-next-line no-await-in-loop
      await settleDialog(FOLLOWUP_CONFIRM_MS);
    }
  }

  // 고정 시간 동안 팝업을 보여 둔 뒤 닫지 않고, 나타나는 순간 내용을 읽고 닫는다.
  // 저장 직전 「선택」 대화에 답한 문구는 Auto(=beforeNotices 를 버리는 경로)에서도
  // 반드시 사람에게 남긴다 — 무엇을 아니오로 답했는지 못 보면 사용자가 알 수 없다.
  const afterNotices = [...answeredBefore, ...approved, ...await dismissNotices(3, NOTICE_MS)];
  // 호출자는 클릭 전 화면 알림과 클릭 결과 알림을 서로 다른 의미로 취급할 수 있다.
  return includeBeforeNotices ? [...beforeNotices, ...afterNotices] : afterNotices;
}

export async function runAction(id: string, includeBeforeNotices = true): Promise<string[]> {
  const normalized = id.toLowerCase();
  if (!READ_ONLY_ACTIONS.includes(normalized as typeof READ_ONLY_ACTIONS[number])) {
    throw new Error(`${id} 는 읽기 전용 목록에 없다`);
  }
  return clickAction(id, includeBeforeNotices);
}

/**
 * 저장·삭제. **Auto 가 켜졌다는 사실을 인자로 받아야만** 누른다.
 *
 * 되돌릴 수 없는 것은 화면이 한 번 더 묻는다(`앱이 확인을 요구한다`). 그 확인은
 * `clickAction` 이 지금까지처럼 거절하지 않고, Auto 일 때는 눌러 준다 — 사용자가
 * 자율을 켠 뜻이 그것이다. 켜지 않았으면 여기서 끝난다.
 */
export async function runAutoAction(id: string, auto: boolean): Promise<string[]> {
  const normalized = id.toLowerCase();
  if (!AUTO_ACTIONS.includes(normalized as typeof AUTO_ACTIONS[number])) {
    throw new Error(`${id} 는 자율 작업 목록에 없다`);
  }
  if (!auto) throw new Error(`${id} 는 Auto 모드가 켜져 있을 때만 할 수 있다`);
  return clickAction(id, false, { confirm: true });
}

/**
 * 앞 걸음이 남긴 확인 대화를 치운다.
 *
 * 저장 뒤 확인이 **뒤늦게** 뜨는 화면이 있다. 인사등록은 기본정보를 저장하고 한참 뒤에
 * `사용자 등록 페이지로 넘어가겠습니까` 를 띄우는데, 그것이 화면에 붙어 있으면 다음
 * 걸음이 `앱이 확인을 요구한다` 로 죽는다 — 가족 탭에서 실제로 그랬다.
 *
 * 무엇을 답할지는 `confirmQuestion` 이 안다(일은 예, 화면 떠나기는 아니오).
 */
export function clearPendingConfirms(limit = 3): string[] {
  const said: string[] = [];
  for (let round = 0; round < limit; round += 1) {
    const answered = confirmQuestion();
    if (!answered) break;
    said.push(answered);
  }
  return said;
}

export async function runDraftAction(id: string): Promise<string[]> {
  const normalized = id.toLowerCase();
  if (!DRAFT_ACTIONS.includes(normalized as typeof DRAFT_ACTIONS[number])) {
    throw new Error(`${id} 는 초안 작업 목록에 없다`);
  }
  // 화면 자동조회가 남긴 "자료 없음" 알림은 신규 액션의 실패가 아니다.
  // 클릭 뒤 새로 나타난 알림만 신규 결과로 돌려준다.
  return clickAction(id, false);
}

/**
 * 화면에 적힌 이름으로 버튼을 눌러 준다. **Auto 일 때만.**
 *
 * ERP 의 문서 연결은 액션 목록에 없다 — 견적서등록의 `자재청구서` 버튼처럼 화면 고유
 * 버튼이고, 누르면 모달이 뜨고 거기서 `확인` 을 눌러야 문서가 만들어진다. 그 두 걸음을
 * 하나로 묶는다. 무엇을 눌렀고 무엇이 떴는지는 전부 돌려준다 — 사용자는 그 화면을
 * 보고 있지 않다.
 */
export async function pressNamed(
  label: string,
  auto: boolean,
  confirmLabel = '확인',
  fields: Record<string, string> = {},
  selectKey?: string,
  /**
   * 물음에 「예」로 답해도 되나 — 화면이 고른 문서를 다시 쥐었나. 상신 단추는 저장 → 목록 다시 읽기 → 물음
   * 순이라, 물음이 뜬 때 화면 문서가 비었거나 **다른 줄**일 수 있다. 그대로 예를 누르면 빈 문서(서버가
   * 「본인이 작성하지 않은 문서」로 막았다)나 남의 문서가 올라간다. 끝내 아니면 아니오로 멈춘다.
   */
  ready?: () => boolean,
): Promise<string[]> {
  if (!auto) throw new Error(`${label} 버튼은 Auto 모드가 켜져 있을 때만 누를 수 있다`);
  const wanted = label.replace(/\s+/g, '');
  const button = Array.from(document.querySelectorAll<HTMLElement>('button'))
    .filter(onScreen)
    .filter((node) => (node.textContent ?? '').replace(/\s+/g, '') === wanted)
    .pop();
  if (!button) throw new Error(`${label} 버튼이 화면에 없다`);
  await waitForCurtain(Date.now() + CURTAIN_MS);
  const before = await dismissNotices();
  button.click();
  await sleep(NOTICE_MS);

  // 모달이 떴으면 그 안의 확인을 누른다. 없으면 버튼 하나로 끝나는 화면이다.
  const notices: string[] = [];
  // 이미 확인을 누른 창 — **요소가 아니라 창의 첫 글자로** 기억한다. 확인 뒤 앱이 창을 다시 그리면 요소가 바뀌어,
  // 같은 창을 새 창으로 알고 칸을 다시 채우고 또 눌렀다(보수현장 담당자변경: 변경은 됐는데 오류로 끝나고, 두 번째
  // 확인이 「최소 하나이상 선택하세요」였다, 2026-10-14).
  const pressedModals = new Set<string>();
  const signature = (modal: HTMLElement) => readText(modal).replace(/\s+/g, ' ').trim().slice(0, 40);
  let deadline = Date.now() + MODAL_WAIT_MS;
  const hardStop = Date.now() + LONG_WORK_MS;
  // **가림막이 덮고 있으면 일이 아직 도는 중이다** — 끝 알림을 놓치지 않게 더 본다. 매니저 일은 가림막이 끊겼다
  // 이어진다: 호기 연동이 끝나 가림막을 내리고, 검사 연동을 매니저에 묻는 동안은 안 덮다가 다시 덮는다. 그 틈에
  // 끝내면 「연동이 완료되었습니다」를 놓친다. 정보센터 단추와 매니저를 띄운 뒤에만 내린 뒤 잠깐(8초) 더 본다 —
  // 다른 단추마다 8초를 더 기다리지 않게.
  let longJob = /정보센터/.test(label);
  let lastCurtain = 0;
  const working = () => {
    if (curtained()) lastCurtain = Date.now();
    return Date.now() < hardStop && (curtained() || (longJob && Date.now() - lastCurtain < 8000));
  };
  while (Date.now() < deadline || working()) {
    // 창 대신 **물음**(`Confirm.ask`)이 뜨는 버튼이 있다 — 지로일괄발행의 전자지로생성.
    // 알림 닫기는 물음을 건드리지 않고, 아래 모달 찾기는 swal 을 안 본다. 그래서 물음이
    // 떠 있는 채로 턴이 끝났고, 답은 앞 조회의 알림 하나를 보고 「눌렀습니다」였다.
    //
    // 단추는 **글자로** 고른다 — 그 화면은 확인 자리에 「아니오」, 취소 자리에 「예」를 둔다.
    // 고르라 한 글자(계획의 confirm)가 없으면 「예」·「확인」으로 답한다: 사람이 이 버튼을
    // 누르라 했고, 그 자리의 사람도 예를 누른다. 다만 「다른 화면으로 가겠는가」 같은 물음은
    // 아니오다(`declines`). 취소 자리를 누르면 안 된다 — 그 화면에선 그것이 「예」였고, 실제로
    // 일이 진행됐는데 답은 「취소했다」였다(2026-10-13).
    const asked = questionAsked();
    if (asked) {
      // **화면이 일을 마칠 때까지 기다렸다 답한다.** 상신 단추는 저장 → 목록 다시 읽기 → 물음 순인데,
      // 물음이 뜬 동안에도 화면은 문서를 다시 읽고 있다. 곧바로 답하면 빈 문서로 상신돼 서버가 「본인이
      // 작성하지 않은 문서」로 막았다(웹소켓 기록, 2026-10-13). 사람은 그 사이 한숨 돌리고 누른다.
      // eslint-disable-next-line no-await-in-loop
      await settle(5000);
      // eslint-disable-next-line no-await-in-loop
      await sleep(300);
      if (ready && !declines(asked)) {
        const until = Date.now() + 8000;
        // eslint-disable-next-line no-await-in-loop
        while (!ready() && Date.now() < until) await sleep(200);
        if (!ready()) {
          answerQuestion(NO_LABELS);
          notices.push(`${label}: 화면이 고른 문서를 다시 잡지 못해 아니오로 멈췄다 — ${asked}`);
          break;
        }
      }
      const answered = declines(asked)
        ? answerQuestion(NO_LABELS)
        : answerQuestion([confirmLabel, ...YES_LABELS]);
      if (answered) {
        notices.push(`${label}: ${answered.message} — 「${answered.pressed}」로 답했다`);
      } else if (answerQuestion(NO_LABELS)) {
        notices.push(`${label}: 답할 단추를 몰라 아니오로 닫았다 — ${asked}`);
        break;
      } else {
        // 글자로 아무것도 못 찾았다. 어느 쪽이 거절인지 모르니 누르지 않고 멈춘다.
        throw new Error(`${label} 뒤 물음에 답할 단추를 찾지 못했다 — ${asked}`);
      }
      if (NO_LABELS.includes(answered.pressed)) break;
      // **예로 답한 뒤에도 창이 이어질 수 있다.** 결재상신은 물음 다음에 결재선 창이 뜬다.
      // 같은 물음을 또 읽지 않게 닫히기를 기다리고, 잠깐 더 본다.
      // eslint-disable-next-line no-await-in-loop
      await settleDialog(1500);
      if (/엘맨\s*매니저/.test(asked)) longJob = true;
      deadline = Date.now() + (longJob && /엘맨\s*매니저/.test(asked) ? MANAGER_WAIT_MS : FOLLOW_MS);
      // eslint-disable-next-line no-continue
      continue;
    }
    // **답할 것 없는 알림은 닫고 계속 본다.** 결재상신 단추는 저장부터 해서 「서버에 잘 저장했습니다」가
    // 먼저 뜨고, 그것을 닫아야 물음이 나온다. 안 닫으면 물음이 시한 뒤에야 떠서 아무도 답하지 않았다.
    // 알림이 끝일 수도 있으니(「선택을 하나라도 해주세요」) 기다림은 짧게 줄인다.
    // eslint-disable-next-line no-await-in-loop
    const shown = await dismissNotices(1, 0);
    if (shown.length) {
      // 같은 알림이 안 닫히고 남는 화면도 있다 — 기다림은 줄이기만 하고 늘리지 않는다.
      notices.push(...shown.filter((text) => !notices.includes(text)));
      deadline = Math.min(deadline, Date.now() + FOLLOW_MS);
      // eslint-disable-next-line no-continue
      continue;
    }
    // 보이는 모달이 여럿이다 — 대부분 빈 껍데기이고 내용은 그중 하나에만 있다.
    // 마지막 것만 보면 껍데기를 보고 "창이 안 떴다" 고 답한다. 실제로 그랬다:
    // 화면 플래그는 켜져 있는데 우리만 못 봤다.
    const modals = Array.from(document.querySelectorAll<HTMLElement>('[class*=ModalView]'))
      .filter(onScreen);
    let modal: HTMLElement | undefined;
    let confirm: HTMLElement | undefined;
    for (const candidate of modals) {
      if (pressedModals.has(signature(candidate))) continue;
      // 창마다 확인 노릇을 하는 단추가 다르다 — 결재선 창은 「닫기」가 저장하고 닫는다.
      const text = readText(candidate);
      const wanted = [confirmLabel, ...MODAL_CONFIRMS.filter((one) => one.title.test(text)).map((one) => one.label)];
      const button = Array.from(candidate.querySelectorAll<HTMLElement>('button'))
        .filter((node) => wanted.includes((node.textContent ?? '').trim()))
        .pop();
      if (button) { modal = candidate; confirm = button; break; }
    }
    if (confirm && modal) {
      notices.push(`${label}: ${readText(modal).replace(/\s+/g, ' ').trim().slice(0, 200)}`);
      // 창이 요구하는 값이 있으면 확인 전에 채운다 — 견적서의 자재청구 창은 비고가
      // 비면 `확인: 비고(요청사항)을 입력하세요` 로 되돌려보낸다.
      if (Object.keys(fields).length) {
        const inModal = modal;
        // eslint-disable-next-line no-await-in-loop
        await withinModal(inModal, () => fillConditions(fields));
        // 창의 검색칸은 **Enter 로** 돈다(`onEnter={() => scope.modalBillRetrieve()}`).
        // 값만 넣고 지나가면 목록이 그대로라, 고를 것을 못 찾는다.
        // eslint-disable-next-line no-await-in-loop
        await withinModal(inModal, async () => { for (const name of Object.keys(fields)) {
          const box = findField(name) && inputOf(findField(name)!);
          if (!box) continue;
          // 검색 창이 걸린 칸의 Enter 는 조회가 아니라 그 창을 다시 연다 — 이미 골랐는데 창이 또 떠, 담당자변경의 표
          // 고르기·확인이 엇나갔다(「최소 하나이상 선택하세요」, 2026-10-14). 그 칸은 고를 때 이미 조회했다.
          if (box.getAttribute('data-el-search')) continue;
          box.dispatchEvent(new KeyboardEvent('keydown', {
            key: 'Enter', keyCode: 13, which: 13, bubbles: true, cancelable: true,
          } as KeyboardEventInit));
        } });
        // eslint-disable-next-line no-await-in-loop
        await sleep(SEARCH_MS);
      }
      // 창의 표에 고를 것이 있으면 전부 고른다 — `확인: 선택을 하나이상 하십시요`.
      // 무엇을 넘길지는 이미 앞 화면이 정했다(그 견적서의 자재). 여기서 다시 고를 것이 없다.
      if (selectKey) {
        const setter = bridge().setCheckedRows;
        if (!setter) throw new Error('이 앱은 창 안 표의 선택 조작을 지원하지 않는다');
        // **표가 찰 때까지 기다린다.** 칸을 고르면 창이 그 조건으로 다시 조회한다(담당자변경: 전담당자를 고르면
        // 그 사람 현장을 불러온다). 빈 표를 체크하고 확인을 눌러 「최소 하나이상 선택하세요」였다(2026-10-14).
        const rowsIn = () => {
          const rows = bridge().pageState()?.[selectKey];
          return Array.isArray(rows) ? rows.length : 0;
        };
        const filledBy = Date.now() + 8000;
        // eslint-disable-next-line no-await-in-loop
        while ((!rowsIn() || (bridge().busy?.() ?? false)) && Date.now() < filledBy) await sleep(200);
        // eslint-disable-next-line no-await-in-loop
        let applied = await setter(selectKey, 'chk', true);
        if (!applied) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(600);
          // eslint-disable-next-line no-await-in-loop
          applied = await setter(selectKey, 'chk', true);
        }
        if (!applied) notices.push(`${label}: 창 안 표 ${selectKey} 를 고르지 못했다`);
      }
      // 창이 자기 자료를 다 받기 전에 누르면 빈 채로 저장된다 — 결재선 창은 0행으로 떴다가 서버의
      // 결재선을 채운다. 거기서 「닫기」를 누르면 「결재자가 최소 한명」으로 막혔다(2026-10-13).
      const ready = MODAL_CONFIRMS.find((one) => one.title.test(readText(modal!)))?.ready;
      if (ready) {
        const until = Date.now() + 4000;
        // eslint-disable-next-line no-await-in-loop
        while (!ready(modal) && Date.now() < until) await sleep(200);
      }
      confirm.click();
      // **창 다음에 물음이 이어질 수 있다** — 견적서복사는 견적일자 창 「확인」 뒤에 「복사 하시겠습니까?」를
      // 묻는다(사람 47/47 예). 여기서 끝내면 물음이 떠 있는 채 턴이 끝났다. 잠깐 더 본다.
      pressedModals.add(signature(modal));
      deadline = Date.now() + FOLLOW_MS;
      // eslint-disable-next-line no-continue
      continue;
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(CONFIRM_POLL_MS);
  }
  // 확인 뒤 뜨는 알림(성공·거절)은 그대로 싣는다.
  const after = await dismissNotices(3, NOTICE_MS);
  // **결재상신은 알림이 없다.** 올라갔는지는 단추가 「상신취소」로 바뀌었는지로 본다.
  if (/결재상신/.test(wanted)) after.push(await reportedSign(label));
  // 창도 안 뜨고 알림도 없으면 **아무 일도 일어나지 않은 것**이다. 조용히 성공으로
  // 넘기면 답변이 `눌렀습니다` 로 끝나고, 사용자는 만들어진 줄 안다 — 견적서의
  // 자재청구서 버튼이 그랬다(청구서는 생기지 않았다).
  if (!notices.length && !after.length) {
    return [...before, `${label}: 창이 뜨지 않았고 화면도 아무 말이 없었다`];
  }
  return [...before, ...notices, ...after];
}

/**
 * **확인 노릇을 하는 단추가 「확인」이 아닌 창.** 제목으로 가린다 — 「닫기」는 대개 취소라 이 표에
 * 오른 창에서만 누른다. 결재선 창(`ModalApproval`)은 저장된 결재선을 미리 채워 열리고, 「닫기」가
 * 결재선을 저장하고 닫아 상신이 이어진다(앱 `ModalApproval.tsx`).
 */
const MODAL_CONFIRMS: { title: RegExp; label: string; ready?: (modal: HTMLElement) => boolean }[] = [
  // 바닥 줄 「총 N행」이 1 이상이 될 때까지 — 결재선이 정말 없으면 4초 뒤 그대로 누르고 앱의 말을 싣는다.
  { title: /결재\s*라인\s*설정/, label: '닫기', ready: (modal) => /총\s*[1-9]\d*\s*행/.test(readText(modal)) },
];

/** 물음·알림에 답한 뒤 이어지는 창을 기다리는 시간. */
const FOLLOW_MS = 3000;

/** 상신 뒤 단추가 「상신취소」로 바뀌었나. 앱은 상신 뒤 다시 조회하니 잠깐 기다린다. */
async function reportedSign(label: string): Promise<string> {
  const deadline = Date.now() + 8000;
  for (;;) {
    const flipped = onScreenAll('button')
      .some((node) => (node.textContent ?? '').replace(/\s+/g, '') === '상신취소');
    if (flipped) return `${label}: 상신취소 단추로 바뀌었다 — 결재가 올라갔다`;
    if (Date.now() >= deadline) return `${label}: 단추가 그대로다 — 올라갔는지 확인하지 못했다`;
    // eslint-disable-next-line no-await-in-loop
    await sleep(300);
  }
}

/** 채널마다 화면 버튼 이름과 모달 제목, 그리고 채울 칸이 다르다. */
/**
 * 채널마다 버튼 이름이 화면마다 다르다.
 *
 * `전송` 하나로 찾다가 문자를 영영 못 보냈다 — 고장접수의 그 버튼은 `문자전송` 이다.
 * 견적서의 카카오 버튼은 아이콘 옆에 `전송` 이라고만 적혀 있다. 아는 이름을 순서대로
 * 시도한다. `name` 은 사용자에게 말할 때 쓰는 이름이다 — `전송 버튼이 화면에 없다` 는
 * 어느 전송인지 말해 주지 않는다.
 */
export const SEND_CHANNELS = {
  email: {
    name: '메일', buttons: ['EMAIL'], title: '이메일 전송',
    to: 'emailTo', subject: 'emailTitle', body: 'emailRemark',
  },
  sms: {
    name: '문자', buttons: ['문자전송', 'SMS', '전송'], title: '문자메시지 전송',
    to: 'smsSearchQuery', body: 'smsRemark',
  },
  kakao: {
    name: '카카오톡', buttons: ['카카오톡전송', '카톡전송', '전송'], title: '카카오톡 전송',
    to: 'kakaoSearchQuery', body: 'kakaoRemark',
  },
  fax: { name: '팩스', buttons: ['FAX'], title: '팩스 전송', to: 'faxTo', body: 'faxRemark' },
} as const;

export type SendChannel = keyof typeof SEND_CHANNELS;

/**
 * 메일·문자·팩스·카카오톡을 보낸다. **Auto 일 때만.**
 *
 * 이것들은 화면 액션이 아니라 전역 모달이다(`ModalStore.openEmail` 등). 화면의 채널
 * 버튼을 눌러 모달을 띄우고, 앵커로 칸을 채운 뒤 `전송` 을 누른다. 무엇을 어디로
 * 보냈는지는 답변에 실어야 한다 — 사용자는 그 모달을 못 봤다.
 *
 * 팩스는 첨부가 필수라(화면이 `보낼 첨부 문서를 1개 이상 등록해주세요` 로 막는다)
 * 여기서 보내지 못한다. 그 사실은 부르는 쪽이 사용자에게 말한다.
 */
export async function sendMessage(
  channel: SendChannel,
  values: {
    to?: string; subject?: string; body?: string;
    /**
     * 전송 창에 닿기 전에 지나야 하는 창의 값.
     *
     * 고장접수의 `문자전송` 은 먼저 **문자유형**(음성문자·문자)을 묻는다. 고르지 않으면
     * 확인을 눌러도 아무 일도 일어나지 않는다 — `문자` 는 `{ gubun: '0' }` 이다.
     */
    fields?: Record<string, string>;
  },
  auto: boolean,
): Promise<string[]> {
  if (!auto) throw new Error('메일·문자·팩스·카카오톡은 Auto 모드가 켜져 있을 때만 보낼 수 있다');
  const spec = SEND_CHANNELS[channel];
  // 아는 이름을 차례로 눌러 본다. 하나도 없으면 그 화면에 그 채널이 없는 것이고,
  // 그 사실은 버튼 이름이 아니라 **채널 이름**으로 말해야 사람이 알아듣는다.
  let notices: string[] = [];
  let pressed = false;
  for (const label of spec.buttons) {
    try {
      // eslint-disable-next-line no-await-in-loop
      notices = await pressNamed(label, auto, '확인', values.fields ?? {});
      pressed = true;
      break;
    } catch (error) {
      if (!/버튼이 화면에 없다$/.test(error instanceof Error ? error.message : '')) throw error;
    }
  }
  if (!pressed) {
    // 카카오톡 버튼은 회사가 알림톡을 쓸 때만 그려진다(`kakaoyn`). 그것을 모르면
    // 사무직 직원은 화면을 아무리 뒤져도 없는 버튼을 찾게 된다.
    const off = channel === 'kakao' && String(bridge().pageState()?.kakaoyn ?? '') === '0';
    throw new Error(off
      ? '이 회사는 카카오톡 알림을 쓰지 않아 전송 버튼이 없다'
      : `이 화면에는 ${spec.name} 전송 버튼이 없다`);
  }

  // 모달이 떴는지는 제목으로 안다. 안 떴으면 그 화면에는 그 채널이 없는 것이다.
  const deadline = Date.now() + MODAL_WAIT_MS;
  let modal: HTMLElement | null = null;
  while (Date.now() < deadline && !modal) {
    modal = Array.from(document.querySelectorAll<HTMLElement>('[class*=ModalView]'))
      .filter(onScreen)
      .find((node) => readText(node).includes(spec.title)) ?? null;
    // eslint-disable-next-line no-await-in-loop
    if (!modal) await sleep(CONFIRM_POLL_MS);
  }
  // 창이 안 뜬 데는 대개 이유가 있고, **화면이 그 이유를 이미 말했다** —
  // 견적서 메일은 `먼저 결재를 받아주세요` 로 막힌다. 그 말을 버리고 "창이 열리지
  // 않았다" 만 전하면 사용자는 무엇을 해야 할지 알 수 없다.
  if (!modal) {
    throw new Error(notices.length
      ? `${spec.title} 창이 열리지 않았다: ${notices.join(' / ')}`
      : `${spec.title} 창이 열리지 않았다`);
  }

  const filled: Record<string, string> = {};
  if (values.to) filled[spec.to] = values.to;
  if (values.subject && 'subject' in spec) filled[(spec as { subject: string }).subject] = values.subject;
  if (values.body) filled[spec.body] = values.body;
  await fillConditions(filled);

  const send = Array.from(modal.querySelectorAll<HTMLElement>('button'))
    .filter((node) => (node.textContent ?? '').trim() === '전송')
    .pop();
  if (!send) throw new Error(`${spec.title} 창에 전송 단추가 없다`);
  send.click();
  const after = await dismissNotices(3, NOTICE_MS);
  return [...notices, ...after];
}
